import {
  addDays,
  addMilliseconds,
  differenceInDays,
  differenceInMilliseconds,
  isValid,
  startOfDay,
} from 'date-fns';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDayTimeInMillis } from '@lmt/unicorn-shared/app/helpers/date-helper';
import clsx from 'clsx';
import { Field } from '../../../Field';
import { DatePicker } from './DatePicker';
import { TimePicker } from './TimePicker';

import styles from './DateTimeIntervalPicker.module.scss';

export interface DateTimeInterval {
  startDate?: Date | null;
  startTimeInMills?: number | null;
  endDate?: Date | null;
  endTimeInMills?: number | null;
}
export function stringifyDateTimeIntervalForMemoization(
  interval: DateTimeInterval,
) {
  if (!interval) return '';
  return `${interval.startDate?.getTime()}${
    interval.startTimeInMills
  }${interval.endDate?.getTime()}${interval.endTimeInMills}`;
}

interface DateTimeIntervalPickerProps {
  mode?: 'single-line' | 'two-lines';
  minDateTime?: Date;
  value: DateTimeInterval;
  onIntervalChanged: (newInterval: DateTimeInterval) => void;
  className?: string;
  datePickerClassName?: string;
  timePickerClassName?: string;
  startDateError?: boolean;
  startDateErrorText?: string;
  startTimeError?: boolean;
  startTimeErrorText?: string;
  endDateError?: boolean;
  endDateErrorText?: string;
  endTimeError?: boolean;
  endTimeErrorText?: string;
  isDateOnly?: boolean;
  required?: boolean;
  setInvalidEndTime?: (value: boolean) => void;
}
const millisInADay = 24 * 60 * 60 * 1000;
export const DateTimeIntervalPicker: FC<DateTimeIntervalPickerProps> = (
  props,
) => {
  const {
    mode = 'single-line',
    minDateTime,
    value,
    onIntervalChanged,
    className,
    datePickerClassName,
    timePickerClassName,
    startDateError,
    startDateErrorText,
    startTimeError,
    startTimeErrorText,
    endDateError,
    endDateErrorText,
    endTimeError,
    endTimeErrorText,
    isDateOnly,
    required,
  } = props;

  const i18n = useTranslation();
  const [lastDiffInDays, setLastDiffInDays] = useState<number>(1);

  const sameStartAndEndDate = useMemo(
    () =>
      value?.startDate &&
      value?.endDate &&
      value.startDate.getTime() === value.endDate.getTime(),
    [value],
  );

  const onStartDateChange = useCallback(
    (newStartDate: Date | null) => {
      const startDate = value.startDate;
      let endDate = value?.endDate;
      if (newStartDate) {
        if (!endDate) {
          endDate = newStartDate;
        } else if (startDate && endDate) {
          if (isValid(startDate) && isValid(endDate)) {
            const diffInDays = differenceInDays(endDate, startDate);
            endDate = addDays(newStartDate, diffInDays);
            setLastDiffInDays(diffInDays);
          } else {
            endDate = addDays(newStartDate, lastDiffInDays);
          }
        }
      }
      const newInterval: DateTimeInterval = {
        startDate: newStartDate,
        startTimeInMills: value?.startTimeInMills,
        endDate,
        endTimeInMills: value?.endTimeInMills,
      };
      onIntervalChanged?.(newInterval);
    },
    [value, onIntervalChanged, lastDiffInDays],
  );

  const onStartTimeChange = useCallback(
    (newStartTimeInMilliseconds: number | null, isInvalid: boolean) => {
      if (isInvalid) {
        onIntervalChanged?.({ ...value, startTimeInMills: NaN });
        return;
      }

      let endTimeInMilliseconds = value?.endTimeInMills;
      if (
        newStartTimeInMilliseconds === null ||
        endTimeInMilliseconds === null ||
        endTimeInMilliseconds === undefined ||
        isNaN(endTimeInMilliseconds)
      ) {
        onIntervalChanged?.({
          ...value,
          startTimeInMills: newStartTimeInMilliseconds,
        });
        return;
      }

      const startTimeInMilliseconds = value?.startTimeInMills;
      let endDate = value?.endDate;
      if (
        startTimeInMilliseconds !== null &&
        startTimeInMilliseconds !== undefined &&
        !isNaN(startTimeInMilliseconds)
      ) {
        // we preserve interval length when we change the start time
        const durationInMilliseconds = differenceInMilliseconds(
          endTimeInMilliseconds,
          startTimeInMilliseconds,
        );
        endTimeInMilliseconds =
          newStartTimeInMilliseconds + durationInMilliseconds;
        if (endTimeInMilliseconds >= millisInADay) {
          endTimeInMilliseconds = endTimeInMilliseconds - millisInADay;
          endDate = addMilliseconds(endDate!, millisInADay);
        }
      } else if (
        sameStartAndEndDate &&
        newStartTimeInMilliseconds > endTimeInMilliseconds
      ) {
        endTimeInMilliseconds = newStartTimeInMilliseconds;
      }
      const newInterval: DateTimeInterval = {
        startDate: value?.startDate,
        startTimeInMills: newStartTimeInMilliseconds,
        endDate: endDate,
        endTimeInMills: endTimeInMilliseconds,
      };
      onIntervalChanged?.(newInterval);
    },
    [value, onIntervalChanged, sameStartAndEndDate],
  );

  const onEndDateChange = useCallback(
    (newEndDate: Date | null) => {
      const startDate = value?.startDate;
      if (newEndDate && startDate && isValid(startDate)) {
        if (newEndDate < startDate) {
          return;
        }
        const startTime = value?.startTimeInMills;
        let endTime = value?.endTimeInMills;
        if (
          startDate?.getTime() === newEndDate?.getTime() &&
          startTime !== null &&
          startTime !== undefined &&
          endTime !== null &&
          endTime !== undefined &&
          startTime > endTime
        ) {
          return;
        }
        const diffInDays = differenceInDays(newEndDate, startDate);
        setLastDiffInDays(diffInDays);
      }
      const newInterval: DateTimeInterval = {
        ...value,
        endDate: newEndDate,
      };
      onIntervalChanged?.(newInterval);
    },
    [value, onIntervalChanged],
  );

  const onEndDateBlur = useCallback(
    (newEndDate: Date | null | undefined) => {
      if (!isValid(newEndDate)) {
        const newInterval: DateTimeInterval = {
          ...value,
          endDate: newEndDate,
        };
        onIntervalChanged?.(newInterval);
      }
      let startDate = value?.startDate;
      const startTime = value?.startTimeInMills;
      let endTime = value?.endTimeInMills;
      if (
        newEndDate &&
        (!startDate || newEndDate < startDate || !isValid(startDate))
      ) {
        startDate = addDays(newEndDate, -lastDiffInDays);
      }
      if (
        startDate &&
        newEndDate &&
        startDate?.getTime() === newEndDate?.getTime() &&
        startTime !== null &&
        startTime !== undefined &&
        endTime !== null &&
        endTime !== undefined &&
        startTime > endTime
      ) {
        endTime = startTime;
      }
      const newInterval: DateTimeInterval = {
        startDate: startDate,
        startTimeInMills: startTime,
        endDate: newEndDate,
        endTimeInMills: endTime,
      };
      onIntervalChanged?.(newInterval);
    },
    [value, onIntervalChanged, lastDiffInDays],
  );

  const onStartDateBlur = useCallback(
    (newStartDate: Date | null | undefined) => {
      if (!isValid(newStartDate)) {
        const newInterval: DateTimeInterval = {
          ...value,
          startDate: newStartDate,
        };
        onIntervalChanged?.(newInterval);
      }
    },
    [value, onIntervalChanged],
  );

  const onEndTimeChange = useCallback(
    (newEndTime: number | null, isInvalid: boolean) => {
      if (isInvalid) {
        onIntervalChanged?.({ ...value, endTimeInMills: NaN });
        return;
      }

      let startTime = value?.startTimeInMills;
      if (
        sameStartAndEndDate &&
        startTime !== null &&
        startTime !== undefined &&
        !isNaN(startTime) &&
        newEndTime !== null &&
        newEndTime < startTime
      ) {
        startTime = newEndTime;
      }

      const newInterval: DateTimeInterval = {
        startDate: value?.startDate,
        startTimeInMills: startTime,
        endDate: value?.endDate,
        endTimeInMills: newEndTime,
      };
      onIntervalChanged?.(newInterval);
    },
    [value, onIntervalChanged, sameStartAndEndDate],
  );

  const isSingleLine = mode === 'single-line';

  const minDate = useMemo(
    () => minDateTime && startOfDay(minDateTime),
    [minDateTime],
  );
  const minTime = useMemo(() => getDayTimeInMillis(minDateTime), [minDateTime]);
  return (
    <div
      className={clsx(
        isSingleLine ? styles.singleLineContainer : styles.twoLinesContainer,
        className,
      )}
    >
      <div className={styles.dateTimeBlock}>
        <Field
          title={i18n.t('uikit.inputs.date_input.starts')}
          required={required}
        >
          <DatePicker
            minDate={minDate}
            containerClassName={datePickerClassName}
            value={value?.startDate || null}
            onDateChanged={onStartDateChange}
            onBlur={onStartDateBlur}
            error={startDateError}
            errorText={startDateErrorText}
            disableInternalErrors={true}
          />
        </Field>
        {!isDateOnly && (
          <TimePicker
            testId={'startTime'}
            minTimeInMills={
              minDate !== undefined &&
              minDate.getTime() === value?.startDate?.getTime()
                ? minTime
                : undefined
            }
            timeInMills={value?.startTimeInMills}
            error={startTimeError}
            errorText={startTimeErrorText}
            onTimeChanged={onStartTimeChange}
            className={clsx(
              isSingleLine
                ? styles.timePickerOneLine
                : styles.timePickerTwoLines,
              timePickerClassName,
            )}
            disableInternalErrors={true}
            ignoreMinTimeError={true}
          />
        )}
      </div>
      {isSingleLine ? <div className={styles.timeSeparator}>—</div> : null}
      <div
        className={clsx(
          styles.dateTimeBlock,
          isSingleLine ? undefined : styles.dateTimeLowerBlock,
        )}
      >
        <Field
          title={i18n.t('uikit.inputs.date_input.ends')}
          required={required}
        >
          <DatePicker
            containerClassName={datePickerClassName}
            value={value?.endDate || null}
            minDate={value?.startDate || minDate}
            onDateChanged={onEndDateChange}
            error={endDateError}
            errorText={endDateErrorText}
            onBlur={onEndDateBlur}
            disableInternalErrors={true}
          />
        </Field>
        {!isDateOnly && (
          <TimePicker
            testId={'endTime'}
            timeInMills={value?.endTimeInMills}
            minTimeInMills={
              sameStartAndEndDate && value?.startTimeInMills
                ? value.startTimeInMills
                : undefined
            }
            error={endTimeError}
            errorText={endTimeErrorText}
            onTimeChanged={onEndTimeChange}
            className={clsx(
              isSingleLine
                ? styles.timePickerOneLine
                : styles.timePickerTwoLines,
              timePickerClassName,
            )}
            disableInternalErrors={true}
            ignoreMinTimeError={true}
          />
        )}
      </div>
    </div>
  );
};
